import { ProspectoApi } from "../api/Prospecto/ProspectoApi";

function validatePass(payload) {
    return (rule, value, callback) => {
        const rules = [
            { test: /.{8,}/, message: "A senha deve ter pelo menos 8 caracteres." },
            { test: /[A-Za-z]/, message: "A senha deve ter pelo menos uma letra." },
            { test: /[0-9]/, message: "A senha deve ter pelo menos um número." }
        ];
        
        for (const rule of rules) {
            if (!rule.test.test(payload.password)) {
                callback(new Error(rule.message));
            }
        }

        callback();
    }
};

function validateMatchPass(payload) {
    return (rule, value, callback) => {
        if (value !== payload.password) {
            callback(new Error('As senhas não se coicidem!'));
        } 

        callback();
    }
};

/**
 * Valida se é um e-mail valido.
 * 
 * @returns
 */
function validateEmail() {
    return (rule, value, callback) => {
        if (!value) {
            callback();
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!emailRegex.test(value)) {
            callback(new Error('Não é um e-mail válido.'));
            return;
        }

        callback();
    };
}

/**
 * Valida a quantidade de caracteres minima necessaria.
 * 
 * @param {number} minQuantity - Quantidade minima de caracteres
 * @param {boolean} ignoreSpecialChars - Ignorar caracteres especiais
 * @param {string | null} message - Mensagem informativa
 * @returns 
 */
function validateMin(minQuantity = 0, message = null, ignoreSpecialChars = true) {
    return (rule, currentValue, callback) => {
        if (!currentValue) {
            callback();
            return;
        }

        let value = String(currentValue);

        if (ignoreSpecialChars) {
            value = value.replace(/[^a-zA-Z0-9À-ÿ]/g, '');
        }

        if (value.length < minQuantity) {
            callback(
                new Error(
                    message || `Mínimo ${minQuantity} caracter(es) necessário.`
                )
            );
            return;
        }


        callback();
    };
}

/**
 * Valida a quantidade de caracteres maxima necessaria.
 * 
 * @param {number} maxQuantity - Quantidade maxima de caracteres
 * @param {boolean} ignoreSpecialChars - Ignorar caracteres especiais
 * @param {string | null} message - Mensagem informativa
 * @returns 
 */
function validateMax(maxQuantity = 15, message = null, ignoreSpecialChars = true) {
    return (rule, currentValue, callback) => {
        if (!currentValue) {
            callback();
            return;
        }

        let value = String(currentValue);

        if (ignoreSpecialChars) {
            value = value.replace(/[^a-zA-Z0-9À-ÿ]/g, '');
        }

        if (value.length > maxQuantity) {
            callback(
                new Error(
                    message || `Máximo ${maxQuantity} caracteres.`
                )
            );
            return;
        }


        callback();
    };
}

/**
 * Valida se o CNPJ informado é válido.
 *
 * @param {string | null} message - Mensagem informativa
 * @returns
 */
function validateCNPJ(message = null) {
    return (rule, currentValue, callback) => {
        if (!currentValue) {
            callback();
            return;
        }

        const value = String(currentValue).replace(/[^\d]/g, '');

        const invalidMessage = message || 'CNPJ inválido.';

        // Verifica tamanho
        if (value.length !== 14) {
            callback(new Error(invalidMessage));
            return;
        }

        // Verifica sequências de dígitos repetidos (00000000000000, 11111111111111, etc.)
        if (/^(\d)\1{13}$/.test(value)) {
            callback(new Error(invalidMessage));
            return;
        }

        const calcCheckDigit = (base) => {
            let weight = base.length - 7;
            let sum = 0;

            for (let i = 0; i < base.length; i++) {
                sum += Number(base[i]) * weight;
                weight -= 1;
                if (weight < 2) {
                    weight = 9;
                }
            }

            const remainder = sum % 11;
            return remainder < 2 ? 0 : 11 - remainder;
        };

        const base12 = value.substring(0, 12);
        const firstDigit = calcCheckDigit(base12);

        const base13 = base12 + String(firstDigit);
        const secondDigit = calcCheckDigit(base13);

        const isValid =
            firstDigit === Number(value[12]) &&
            secondDigit === Number(value[13]);

        if (!isValid) {
            callback(new Error(invalidMessage));
            return;
        }

        callback();
    };
}

/**
 * Valida se o CNPJ Alfanumerico informado é válido.
 *
 * @param {string | null} message - Mensagem informativa
 * @returns 
 */
function validateCNPJAlphanumeric(message = null) {
    return (rule, currentValue, callback) => {
        if (!currentValue) {
            callback();
            return;
        }

        const invalidMessage = message || 'CNPJ inválido.';

        const value = String(currentValue)
            .toUpperCase()
            .replace(/[^A-Z0-9]/g, '');

        // CNPJ possui exatamente 14 caracteres
        if (value.length !== 14) {
            callback(new Error(invalidMessage));
            return;
        }

        // As 12 primeiras posições podem ser A-Z ou 0-9.
        // As duas últimas posições devem ser numéricas.
        if (!/^[A-Z0-9]{12}\d{2}$/.test(value)) {
            callback(new Error(invalidMessage));
            return;
        }

        const calculateCheckDigit = (base) => {
            const weights = base.length === 12
                ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
                : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

            const sum = base
                .split('')
                .reduce((total, char, index) => {
                    const value = char.charCodeAt(0) - 48;

                    return total + (value * weights[index]);
                }, 0);

            const remainder = sum % 11;

            return remainder === 0 || remainder === 1
                ? 0
                : 11 - remainder;
        };

        const base12 = value.substring(0, 12);

        const firstDigit = calculateCheckDigit(base12);

        const base13 = base12 + firstDigit;

        const secondDigit = calculateCheckDigit(base13);

        const isValid =
            firstDigit === Number(value[12]) &&
            secondDigit === Number(value[13]);

        if (!isValid) {
            callback(new Error(invalidMessage));
            return;
        }

        callback();
    };
}

/**
 * Valida se o CPF informado é válido.
 *
 * @param {string | null} message - Mensagem informativa
 * @returns
 */
function validateCPF(message = null) {
    return (rule, currentValue, callback) => {
        if (!currentValue) {
            callback();
            return;
        }

        const value = String(currentValue).replace(/[^\d]/g, '');
        const invalidMessage = message || 'CPF inválido.';

        // Verifica tamanho
        if (value.length !== 11) {
            callback(new Error(invalidMessage));
            return;
        }

        // Verifica sequências de dígitos repetidos (00000000000, 11111111111, etc.)
        if (/^(\d)\1{10}$/.test(value)) {
            callback(new Error(invalidMessage));
            return;
        }

        const calcCheckDigit = (base) => {
            let sum = 0;

            for (let i = 0; i < base.length; i++) {
                sum += Number(base[i]) * (base.length + 1 - i);
            }

            const remainder = (sum * 10) % 11;
            return remainder === 10 ? 0 : remainder;
        };

        const base9 = value.substring(0, 9);
        const firstDigit = calcCheckDigit(base9);

        const base10 = base9 + String(firstDigit);
        const secondDigit = calcCheckDigit(base10);

        const isValid =
            firstDigit === Number(value[9]) &&
            secondDigit === Number(value[10]);

        if (!isValid) {
            callback(new Error(invalidMessage));
            return;
        }

        callback();
    };
}

/**
 * Valida a inscricao estadual de acordo com a UF
 * 
 * @returns 
 */
function validateIE(uf, ie) {
    return async (rule, currentValue, callback) => {
        const prospectoApi = new ProspectoApi();

        try {
          const res = await prospectoApi.verificarIE(uf, ie)

          if(res.data == true) {
            return callback();
          }
          return callback(new Error('Inscrição estadual inválida'));
        } catch (error) {
          return callback(new Error('Erro ao verificar inscrição estadual'));
        }
    };
}

/**
 * Valida o valor numérico mínimo necessário.
 *
 * @param {number} minValue - Valor mínimo permitido
 * @param {string | null} message - Mensagem informativa
 * @param {boolean} allowEqual - Permite que o valor seja igual ao mínimo
 * @returns {(rule: any, currentValue: any, callback: Function) => void}
 */
function validateMinNumber(minValue = 0, message = null, allowEqual = true) {
    return (rule, currentValue, callback) => {
        // Campo vazio: deixa a validação de "obrigatório" cuidar disso
        if (currentValue === null || currentValue === undefined || currentValue === '') {
            callback();
            return;
        }

        const value = parseNumber(currentValue);

        if (Number.isNaN(value)) {
            callback(new Error('Informe um número válido.'));
            return;
        }

        const isInvalid = allowEqual ? value < minValue : value <= minValue;

        if (isInvalid) {
            callback(
                new Error(
                    message ||
                    (allowEqual
                        ? `O valor mínimo é ${minValue}.`
                        : `O valor deve ser maior que ${minValue}.`)
                )
            );
            return;
        }

        callback();
    };
}

/**
 * Valida o valor numérico máximo permitido.
 *
 * @param {number} maxValue - Valor máximo permitido
 * @param {string | null} message - Mensagem informativa
 * @param {boolean} allowEqual - Permite que o valor seja igual ao máximo
 */
function validateMaxNumber(maxValue = 0, message = null, allowEqual = true) {
    return (rule, currentValue, callback) => {
        if (currentValue === null || currentValue === undefined || currentValue === '') {
            callback();
            return;
        }

        const value = parseNumber(currentValue);

        if (Number.isNaN(value)) {
            callback(new Error('Informe um número válido.'));
            return;
        }

        const isInvalid = allowEqual ? value > maxValue : value >= maxValue;

        if (isInvalid) {
            callback(
                new Error(
                    message ||
                    (allowEqual
                        ? `O valor máximo é ${maxValue}.`
                        : `O valor deve ser menor que ${maxValue}.`)
                )
            );
            return;
        }

        callback();
    };
}

/**
 * Converte number ou string (inclusive no formato brasileiro "1.234,56") para number.
 */
function parseNumber(input) {
    if (typeof input === 'number') return input;

    let str = String(input).trim();

    // Formato brasileiro: remove separador de milhar e troca vírgula por ponto
    if (str.includes(',')) {
        str = str.replace(/\./g, '').replace(',', '.');
    }

    return str === '' ? NaN : Number(str);
}

/**
 * Valida o tipo do valor informado.
 *
 * @param {'string' | 'number' | 'both'} type - Tipo aceito
 * @param {string | null} message - Mensagem informativa
 * @param {boolean} strictNumber - Se true, string numérica ("123") NÃO conta como número
 * @returns {(rule: any, currentValue: any, callback: Function) => void}
 */
function validateType(type = 'both', message = null, strictNumber = true) {
    const defaultMessages = {
        string: 'Informe um texto válido.',
        number: 'Informe um número válido.',
        both: 'Informe um texto ou número válido.',
    };

    const isString = (v) => typeof v === 'string';

    const isNumber = (v) => {
        if (typeof v === 'number') {
            return Number.isFinite(v);
        }

        // Modo não estrito: aceita string numérica (inclusive "1.234,56")
        if (!strictNumber && typeof v === 'string') {
            return !Number.isNaN(parseNumber(v));
        }

        return false;
    };

    return (rule, currentValue, callback) => {
        // Campo vazio: deixa a validação de "obrigatório" cuidar disso
        if (currentValue === null || currentValue === undefined || currentValue === '') {
            callback();
            return;
        }

        let isValid;

        switch (type) {
            case 'string':
                isValid = isString(currentValue);
                break;
            case 'number':
                isValid = isNumber(currentValue);
                break;
            case 'both':
                isValid = isString(currentValue) || isNumber(currentValue);
                break;
            default:
                callback(new Error(`Tipo de validação inválido: "${type}".`));
                return;
        }

        if (!isValid) {
            callback(new Error(message || defaultMessages[type]));
            return;
        }

        callback();
    };
}

/**
 * Valida quais tipos de caracteres a string pode conter.
 *
 * @param {'string' | 'number' | 'both'} type - 'string' = só letras, 'number' = só dígitos, 'both' = letras e dígitos
 * @param {string | null} message - Mensagem informativa
 * @param {boolean} allowSpecialChars - Permite caracteres especiais (ex.: . - / @ !)
 * @param {boolean} allowSpaces - Permite espaços
 * @returns {(rule: any, currentValue: any, callback: Function) => void}
 */
function validateCharacters(type = 'string', message = null, allowSpecialChars = false, allowSpaces = true) {
    const letters = '\\p{L}';   // Letras de qualquer idioma, inclusive acentuadas
    const digits = '0-9';

    const allowedByType = {
        string: letters,
        number: digits,
        both: letters + digits,
    };

    const defaultMessages = {
        string: 'Use apenas letras.',
        number: 'Use apenas números.',
        both: 'Use apenas letras e números.',
    };

    return (rule, currentValue, callback) => {
        // Campo vazio: deixa a validação de "obrigatório" cuidar disso
        if (currentValue === null || currentValue === undefined || currentValue === '') {
            callback();
            return;
        }

        if (!allowedByType[type]) {
            callback(new Error(`Tipo de validação inválido: "${type}".`));
            return;
        }

        let value = String(currentValue);

        // Remove o que é permitido, para sobrar só o que deve ser validado
        if (allowSpaces) {
            value = value.replace(/\s/g, '');
        }

        if (allowSpecialChars) {
            // Especial = tudo que não é letra, dígito ou espaço
            value = value.replace(/[^\p{L}0-9\s]/gu, '');
        }

        const regex = new RegExp(`^[${allowedByType[type]}]*$`, 'u');

        if (!regex.test(value)) {
            let defaultMessage = defaultMessages[type];

            if (!allowSpecialChars) {
                defaultMessage = defaultMessage.replace(/\.$/, '') + ', sem caracteres especiais.';
            }

            callback(new Error(message || defaultMessage));
            return;
        }

        callback();
    };
}

function validateMatchPassword(password) {
    return (rule, value, callback) => {
        if (value !== password) {
            callback(new Error('As senhas não se coicidem!'));
        } 

        callback();
    }
};

function validatePassword(required = true) {
    return (rule, value, callback) => {
        if(required) {
            const rules = [
                {
                    test: /.{8,}/,
                    message: "A senha deve ter pelo menos 8 caracteres."
                },
                {
                    test: /[A-Za-z]/,
                    message: "A senha deve ter pelo menos uma letra."
                },
                {
                    test: /[0-9]/,
                    message: "A senha deve ter pelo menos um número."
                }
            ];
        
            for (const passwordRule of rules) {
                if (!passwordRule.test.test(value)) {
                    return callback(new Error(passwordRule.message));
                }
            }
        
            callback();
        }

        callback();
    }
}

export {
    validatePass,
    validateMatchPass,
    validateEmail,
    validateMin,
    validateMax,
    validateCNPJ,
    validateCNPJAlphanumeric,
    validateIE,
    validateMinNumber,
    validateMaxNumber,
    validateCPF,
    validateType,
    validateCharacters,
    validatePassword,
    validateMatchPassword
}


// WEBPACK FOOTER //
// ./resources/assets/js/schemas/generalSchema.js